// sbc-live-v2
import { useState, useEffect, useCallback } from "react";
import api from "../api";

async function safe(fn) { try { return await fn(); } catch { return null; } }

const TRANSPORTES = ["udp","tcp","tls","ws","wss"];

// ── SBC status helpers ────────────────────────────────────────────────────────
function sbcBadgeClass(status) {
  if (status === "active")   return "nv-badge-ok";
  if (status === "probing")  return "nv-badge-warn";
  if (status === "inactive") return "nv-badge-err";
  return "nv-badge-muted";
}
function sbcLabel(status) {
  if (status === "active")   return "Activo";
  if (status === "probing")  return "Probando…";
  if (status === "inactive") return "Inactivo";
  return "Sin señal";
}

// ── Alert ─────────────────────────────────────────────────────────────────────
function Alert({ msg, onClose }) {
  if (!msg) return null;
  const cls = {success:"nv-alert-ok",error:"nv-alert-err",warn:"nv-alert-warn"}[msg.type]||"nv-alert-ok";
  return (
    <div className={"nv-alert "+cls} style={{ marginBottom:14 }}>
      <span style={{ flex:1 }}>{msg.text}</span>
      <button onClick={onClose} style={{ background:"none",border:"none",cursor:"pointer",color:"inherit",fontSize:14 }}>✕</button>
    </div>
  );
}

// ── TrunkModal ────────────────────────────────────────────────────────────────
function TrunkModal({ trunk, onClose, onSave }) {
  const isEdit = !!trunk?.id;
  const empty = { nombre:"",proveedor:"",host:"",usuario:"",password:"",prefijo_salida:"0",canales_max:30,transporte:"udp" };
  const [form,setForm] = useState(isEdit ? {...empty,...trunk} : empty);
  const [busy,setBusy] = useState(false);
  const [err,setErr]   = useState(null);
  const [showPwd,setShowPwd] = useState(false);
  const [probe,setProbe] = useState(null);
  const [probing,setProbing] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));

  const probeNow = async () => {
    if (!form.host) { setProbe({ error:"Ingresá un host primero" }); return; }
    setProbing(true); setProbe(null);
    try {
      const r = await api.post("/trunks/probe", { host:form.host, transport:form.transporte });
      setProbe(r.data);
    } catch(e) {
      setProbe({ error:e?.response?.data?.detail || "Error al probar" });
    } finally { setProbing(false); }
  };

  const submit = async () => {
    if (!form.nombre || !form.host) return setErr("Nombre y host son requeridos");
    setBusy(true); setErr(null);
    try {
      if (isEdit) await api.put("/trunks/"+trunk.id, form);
      else        await api.post("/trunks", form);
      onSave();
    } catch(e) { setErr(e?.response?.data?.detail||"Error al guardar"); }
    finally { setBusy(false); }
  };

  return (
    <div className="nv-modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="nv-modal" style={{ maxWidth:580 }}>
        <div className="nv-modal-header">
          <span className="nv-modal-title">{isEdit?"Editar trunk "+trunk.nombre:"Nuevo trunk SIP"}</span>
          <button className="nv-modal-close" onClick={onClose}>✕</button>
        </div>
        {err && <div className="nv-alert nv-alert-err" style={{ marginBottom:14 }}>{err}</div>}
        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Nombre *</label>
            <input className="nv-input" value={form.nombre} onChange={e=>set("nombre",e.target.value)} placeholder="ej: CNT-Principal"/>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Proveedor</label>
            <input className="nv-input" value={form.proveedor||""} onChange={e=>set("proveedor",e.target.value)} placeholder="ej: CNT EP"/>
          </div>
        </div>
        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Host / IP *</label>
            <div style={{ display:"flex", gap:6 }}>
              <input className="nv-input" value={form.host} onChange={e=>set("host",e.target.value)} placeholder="ej: sip.proveedor.com" style={{ fontFamily:"var(--font-mono)", flex:1 }}/>
              <button type="button" onClick={probeNow} disabled={probing} title="Probar conectividad SIP desde el PBX"
                style={{ padding:"0 10px", border:"1px solid var(--border)", borderRadius:"var(--r-sm)", background:"var(--bg-raised)", color:"var(--text)", cursor:probing?"wait":"pointer", fontSize:12, whiteSpace:"nowrap" }}>
                {probing ? "..." : "🔍 Probar"}
              </button>
            </div>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Transporte</label>
            <select className="nv-select" value={form.transporte} onChange={e=>set("transporte",e.target.value)}>
              {TRANSPORTES.map(t=><option key={t} value={t}>{t.toUpperCase()}</option>)}
            </select>
          </div>
        </div>
        {probe && (
          <div style={{ marginBottom:14, padding:"10px 12px", borderRadius:"var(--r-sm)", border:"1px solid var(--border)", background:"var(--bg-raised)", fontSize:12, lineHeight:1.6 }}>
            {probe.error ? (
              <span style={{ color:"var(--danger,#e55)" }}>⚠ {probe.error}</span>
            ) : (
              <>
                <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>
                  <span>
                    <strong>DNS:</strong>{" "}
                    {probe.dns?.ok
                      ? <span style={{ color:"var(--success,#2c8)" }}>{probe.dns.resolved} ({probe.dns.rtt_ms}ms)</span>
                      : <span style={{ color:"var(--danger,#e55)" }}>{probe.dns?.error||"falló"}</span>}
                  </span>
                  <span>
                    <strong>SIP:</strong>{" "}
                    {probe.sip?.ok
                      ? <span style={{ color: (probe.sip.code==="200"||probe.sip.code==="401"||probe.sip.code==="403"||probe.sip.code==="404") ? "var(--success,#2c8)" : "var(--warn,#fb3)" }}>
                          {probe.sip.code} {probe.sip.reason} · {probe.sip.rtt_ms}ms
                        </span>
                      : <span style={{ color:"var(--danger,#e55)" }}>{probe.sip?.error||"sin respuesta"}</span>}
                  </span>
                  {probe.sip?.user_agent && <span style={{ color:"var(--text-muted)" }}><strong>UA:</strong> {probe.sip.user_agent}</span>}
                </div>
                {probe.input && <div style={{ color:"var(--text-muted)", fontSize:10, marginTop:4, fontFamily:"var(--font-mono)" }}>
                  → {probe.input.host}:{probe.input.port}/{probe.input.transport} (desde Soft Switch 192.168.182.20)
                </div>}
              </>
            )}
          </div>
        )}
        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Usuario SIP</label>
            <input className="nv-input" value={form.usuario||""} onChange={e=>set("usuario",e.target.value)} placeholder="Autenticación"/>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Password SIP</label>
            <div style={{ position:"relative" }}>
              <input className="nv-input" type={showPwd?"text":"password"} value={form.password||""} onChange={e=>set("password",e.target.value)} style={{ paddingRight:36 }}/>
              <button type="button" onClick={()=>setShowPwd(p=>!p)} style={{ position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"var(--text-muted)",cursor:"pointer",fontSize:13 }}>{showPwd?"○":"●"}</button>
            </div>
          </div>
        </div>
        <div className="nv-form-row">
          <div className="nv-form-field">
            <label className="nv-label">Prefijo salida</label>
            <input className="nv-input" value={form.prefijo_salida} onChange={e=>set("prefijo_salida",e.target.value)} placeholder="0" style={{ fontFamily:"var(--font-mono)" }}/>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Canales máx.</label>
            <input className="nv-input" type="number" min="1" max="9999" value={form.canales_max} onChange={e=>set("canales_max",parseInt(e.target.value)||30)}/>
          </div>
        </div>
        <div className="nv-modal-footer">
          <button className="nv-btn nv-btn-ghost" onClick={onClose}>Cancelar</button>
          <button className="nv-btn nv-btn-primary" onClick={submit} disabled={busy}>
            {busy?<span className="nv-spinner"/>:(isEdit?"✓ Guardar cambios":"+ Crear trunk")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── TrunkCard ─────────────────────────────────────────────────────────────────
function TrunkCard({ trunk, cdrStats, sbcInfo, sbcLive, onEdit, onToggle, onDelete }) {
  const adminActive = trunk.activo === "yes";
  const calls  = cdrStats?.llamadas || 0;
  const mins   = cdrStats?.minutos  || 0;

  // SBC real-time state (when available)
  const hasSbc    = sbcLive && sbcInfo;
  const sbcStatus = hasSbc ? sbcInfo.status  : null;
  const latency   = hasSbc ? sbcInfo.rtt : null;
  const transport = hasSbc ? sbcInfo.transport : null;

  // Border colour: SBC wins when live, otherwise fall back to DB
  const borderColor = hasSbc
    ? (sbcStatus==="active" ? "var(--success)" : sbcStatus==="probing" ? "var(--warning)" : "var(--danger)")
    : (adminActive ? "var(--success)" : "var(--border)");

  // Icon
  const icon = hasSbc
    ? (sbcStatus==="active" ? "⊕" : sbcStatus==="probing" ? "⊙" : "⊝")
    : (adminActive ? "⊕" : "⊝");

  const iconBg = hasSbc
    ? (sbcStatus==="active" ? "var(--success-bg)" : sbcStatus==="probing" ? "rgba(245,166,35,.12)" : "rgba(255,71,87,.12)")
    : (adminActive ? "var(--success-bg)" : "var(--bg-raised)");

  return (
    <div style={{
      background:"var(--bg-surface)", border:"1px solid var(--border)",
      borderRadius:"var(--r-lg)", padding:"16px 18px",
      borderLeft:"3px solid "+borderColor,
      transition:"border-color .4s",
    }}>
      {/* Header row */}
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{
            width:40, height:40, borderRadius:"var(--r-sm)",
            background:iconBg,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:18, flexShrink:0, transition:"background .4s",
          }}>{icon}</div>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)" }}>{trunk.nombre}</div>
            <div style={{ fontSize:10, color:"var(--text-muted)", fontFamily:"var(--font-mono)", marginTop:1 }}>{trunk.host}</div>
          </div>
        </div>

        {/* Badges: SBC real-time + admin */}
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
          {hasSbc ? (
            <span className={"nv-badge "+sbcBadgeClass(sbcStatus)}>
              <span className="dot"/>{sbcLabel(sbcStatus)}
            </span>
          ) : (
            <span className={"nv-badge "+(adminActive?"nv-badge-ok":"nv-badge-muted")}>
              <span className="dot"/>{adminActive?"Habilitada":"Deshabilitada"}
            </span>
          )}
          {/* Show admin override label when SBC is live and disagrees */}
          {hasSbc && !adminActive && (
            <span style={{ fontSize:9, color:"var(--text-muted)", background:"var(--bg-raised)", padding:"2px 6px", borderRadius:4, border:"1px solid var(--border)" }}>
              Admin: deshabilitado
            </span>
          )}
        </div>
      </div>

      {/* Metric grid */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14 }}>
        {[
          { lbl:"Proveedor",     val:trunk.proveedor||"—",       mono:false },
          { lbl:"Transporte",    val:(trunk.transporte||"udp").toUpperCase(), mono:false },
          { lbl:"Canales máx.",  val:trunk.canales_max||"—",     mono:true  },
          { lbl:"Prefijo",       val:trunk.prefijo_salida||"0",  mono:true  },
          { lbl:"Llamadas (30d)",val:calls.toLocaleString(),      mono:false },
          { lbl:"Minutos (30d)", val:Math.round(mins)+" min",    mono:false },
        ].map(({lbl,val,mono})=>(
          <div key={lbl} style={{ background:"var(--bg-raised)", borderRadius:"var(--r-sm)", padding:"8px 10px" }}>
            <div style={{ fontSize:9, textTransform:"uppercase", letterSpacing:".07em", color:"var(--text-muted)", marginBottom:2 }}>{lbl}</div>
            <div style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)", fontFamily:mono?"var(--font-mono)":"inherit" }}>{val}</div>
          </div>
        ))}
      </div>

      {/* SBC detail bar — only when SBC is live */}
      {hasSbc && (
        <div style={{
          display:"flex", gap:12, marginBottom:12,
          padding:"7px 10px", borderRadius:"var(--r-sm)",
          background:"var(--bg-raised)", border:"1px solid var(--border)",
          fontSize:11, color:"var(--text-secondary)",
        }}>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ color:"var(--text-muted)", fontSize:10 }}>VÍA</span>
            <span style={{ fontFamily:"var(--font-mono)", fontWeight:700, color:"var(--brand)" }}>{transport?transport.toUpperCase():"DIRECTO"}</span>
          </span>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}>
            <span style={{ color:"var(--text-muted)", fontSize:10 }}>LATENCIA</span>
            <span style={{ fontFamily:"var(--font-mono)", fontWeight:700,
              color: latency==null ? "var(--danger)" : latency<80 ? "var(--success)" : latency<200 ? "var(--warning)" : "var(--danger)" }}>
              {latency!=null ? latency+"ms" : "—"}
            </span>
          </span>
          <span style={{ marginLeft:"auto", fontSize:10, color:"var(--text-muted)" }}>
            Carrier directo (LAN)
          </span>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display:"flex", gap:6 }}>
        <button className="nv-btn nv-btn-ghost nv-btn-sm" style={{ flex:1 }} onClick={()=>onEdit(trunk)}>✎ Editar</button>
        <button className={"nv-btn nv-btn-sm "+(adminActive?"nv-btn-warning":"nv-btn-secondary")} onClick={()=>onToggle(trunk)}
          style={{ background:adminActive?"var(--warning-bg)":"var(--bg-raised)", color:adminActive?"var(--warning)":"var(--text-secondary)", borderColor:adminActive?"rgba(245,166,35,.3)":"var(--border)" }}>
          {adminActive?"⏸ Desactivar":"▶ Activar"}
        </button>
        <button className="nv-btn nv-btn-danger nv-btn-sm" onClick={()=>onDelete(trunk)} title="Eliminar">✕</button>
      </div>
    </div>
  );
}

// ── Carriers page ─────────────────────────────────────────────────────────────
export default function Carriers() {
  const [data,   setData]   = useState([]);
  const [stats,  setStats]  = useState({});
  const [loading,setLoading]= useState(true);
  const [modal,  setModal]  = useState(null);
  const [target, setTarget] = useState(null);
  const [msg,    setMsg]    = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [delConf,setDelConf]= useState(null);
  const [confirmTrunk,setConfirmTrunk]= useState(null);  // troncal pendiente de desactivar (confirmación tipeada)
  const [confirmWord, setConfirmWord] = useState("");

  // Carriers REALES del SBC (dispatcher de Kamailio) — sección propia, NO se cruza
  // con las troncales de config (son cosas distintas).
  const [sbcCarriers, setSbcCarriers] = useState([]);
  const [sbcLive,     setSbcLive]     = useState(false);
  const [sbcTs,       setSbcTs]       = useState(null);

  const showMsg = (text,type="success") => { setMsg({text,type}); setTimeout(()=>setMsg(null),4000); };

  // ── Load trunks from DB ──────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    const [tr, cdr] = await Promise.all([
      safe(()=>api.get("/trunks")),
      safe(()=>api.get("/metricas/por-contexto?meses=1")),
    ]);
    setData(tr?.data?.data||[]);
    const st = {};
    (cdr?.data?.data||[]).forEach(r=>{ st[r.contexto]={llamadas:r.llamadas,minutos:r.minutos}; });
    setStats(st);
    setLoading(false);
  }, []);

  useEffect(()=>{ load(); },[load]);

  // ── Poll SBC API every 10 s ──────────────────────────────────────────────
  const loadSbc = useCallback(async () => {
    // Estado SIP live de los carriers vía /noc/carriers (panel-api). Arquitectura directa:
    // el panel-api mide cada troncal de netvoice.trunks con un SIP OPTIONS probe (el SBC
    // ya NO rutea carriers). status="active" = el carrier responde.
    try {
      const r = await api.get("/noc/carriers");
      const d = r.data;
      if (d?.status !== "online") { setSbcLive(false); setSbcCarriers([]); return; }
      setSbcCarriers(d.carriers || []);
      setSbcLive(true);
      setSbcTs(new Date().toISOString());
    } catch {
      setSbcLive(false);
    }
  }, []);

  useEffect(()=>{
    loadSbc();
    const iv = setInterval(loadSbc, 10000);
    return ()=>clearInterval(iv);
  }, [loadSbc]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  // Desactivar exige confirmación tipeada ("desactivar"); activar es directo.
  const handleToggle = (trunk) => {
    if (trunk.activo === "yes") { setConfirmWord(""); setConfirmTrunk(trunk); }
    else doToggle(trunk);
  };
  const doToggle = async (trunk) => {
    try {
      await api.put("/trunks/"+trunk.id, { activo: trunk.activo==="yes"?"no":"yes" });
      showMsg("Troncal "+(trunk.activo==="yes"?"deshabilitada":"habilitada"));
      load();
    } catch(e) { showMsg(e?.response?.data?.detail||"Error","error"); }
  };

  const handleDelete = async (trunk) => {
    try {
      await api.delete("/trunks/"+trunk.id);
      showMsg("Trunk "+trunk.nombre+" eliminado");
      load();
    } catch(e) { showMsg(e?.response?.data?.detail||"Error al eliminar","error"); }
    setDelConf(null);
  };

  // ── Filter ───────────────────────────────────────────────────────────────
  const filtered = data.filter(t=>{
    const q = search.toLowerCase();
    const matchQ = !q||t.nombre?.toLowerCase().includes(q)||t.host?.toLowerCase().includes(q)||t.proveedor?.toLowerCase().includes(q);
    // Troncales = estado de CONFIG (BD). El estado live del SBC va en su sección propia.
    let matchF = true;
    if (filter==="active")   matchF = t.activo==="yes";
    if (filter==="inactive") matchF = t.activo!=="yes";
    return matchQ&&matchF;
  });

  // ── KPI counts (estado de config en BD) ──────────────────────────────────
  const activosCount   = data.filter(t=>t.activo==="yes").length;
  const inactivosCount = data.length - activosCount;
  const totalCanales   = data.filter(t=>t.activo==="yes").reduce((s,t)=>s+(t.canales_max||0),0);

  return (
    <div>
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Carriers / Troncales SIP</div>
          <div className="nv-page-sub">
            {data.length} troncales de config · {activosCount} habilitadas
          </div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={()=>{ load(); loadSbc(); }} disabled={loading}>
            {loading?<span className="nv-spinner"/>:"↺"} Actualizar
          </button>
          <button className="nv-btn nv-btn-primary" onClick={()=>setModal("crear")}>+ Nuevo trunk</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="nv-kpi-grid" style={{ marginBottom:16 }}>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Total trunks</div>
          <div className="nv-kpi-value" style={{ color:"var(--brand)" }}>{data.length}</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Habilitadas</div>
          <div className="nv-kpi-value" style={{ color:"var(--success)" }}>{activosCount}</div>
          <div className="nv-kpi-sub">Estado de config (BD)</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Deshabilitadas</div>
          <div className="nv-kpi-value" style={{ color:inactivosCount?"var(--danger)":"var(--text-muted)" }}>{inactivosCount}</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Canales totales</div>
          <div className="nv-kpi-value" style={{ color:"var(--info)" }}>{totalCanales}</div>
          <div className="nv-kpi-sub">En troncales habilitadas</div>
        </div>
      </div>

      {/* Carriers con estado en vivo (SIP OPTIONS probe del panel-api). Distinto del pool
          de troncales de config de abajo. Arquitectura directa: el SBC no rutea carriers. */}
      <div className="nv-card" style={{ marginBottom:16 }}>
        <div className="nv-card-header">
          <span className="nv-card-title">◈ Carriers — estado en vivo (interconexión directa)</span>
          <span style={{ fontSize:11, color: sbcLive?"var(--success)":"var(--text-muted)" }}>
            {sbcLive
              ? `● en vivo${sbcTs?" · "+new Date(sbcTs).toLocaleTimeString("es-EC",{hour12:false}):""}`
              : "○ sin respuesta"}
          </span>
        </div>
        {sbcCarriers.length===0 ? (
          <div style={{ padding:"10px 0", color:"var(--text-muted)", fontSize:12 }}>
            {sbcLive ? "Sin troncales de carrier activas." : "No se pudo leer el estado de carriers (panel-api)."}
          </div>
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:10 }}>
            {sbcCarriers.map(c=>(
              <div key={c.id} style={{ background:"var(--bg-raised)", border:"1px solid var(--border)", borderRadius:"var(--r-sm)", padding:"10px 12px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <span style={{ fontSize:13, fontWeight:600, color:"var(--text-primary)" }}>{c.name}</span>
                  <span className={"nv-badge "+(c.status==="active"?"nv-badge-ok":c.status==="probing"?"nv-badge-warn":"nv-badge-muted")}>
                    <span className="dot"/>{c.status==="active"?"Activo":c.status==="probing"?"Probando":"Inactivo"}
                  </span>
                </div>
                <div style={{ fontSize:11, color:"var(--text-muted)", fontFamily:"var(--font-mono)" }}>
                  {(c.transport||"udp").toUpperCase()} {c.ip}:{c.port}{c.rtt!=null?` · ${c.rtt} ms`:""} · directo
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Pool de troncales configurables (netvoice.trunks) — para activación de clientes ── */}
      <div style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:".1em",
        color:"var(--text-muted)", marginBottom:10 }}>
        Troncales configuradas (pool de activación)
      </div>

      <Alert msg={msg} onClose={()=>setMsg(null)}/>

      {/* Search + filter */}
      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <div className="nv-search" style={{ flex:1, minWidth:200 }}>
          <span style={{ color:"var(--text-muted)", fontSize:12 }}>⌕</span>
          <input placeholder="Buscar por nombre, host o proveedor..." value={search} onChange={e=>setSearch(e.target.value)}/>
          {search&&<button onClick={()=>setSearch("")} style={{ background:"none",border:"none",color:"var(--text-muted)",cursor:"pointer" }}>✕</button>}
        </div>
        <div style={{ display:"flex", gap:4 }}>
          {[["all","Todas"],["active","Habilitadas"],["inactive","Deshabilitadas"]].map(([k,l])=>(
            <button key={k} className={"nv-btn nv-btn-sm "+(filter===k?"nv-btn-secondary":"nv-btn-ghost")} onClick={()=>setFilter(k)}>{l}</button>
          ))}
        </div>
      </div>

      {/* Cards */}
      {loading&&!data.length ? (
        <div className="nv-loading"><span className="nv-spinner"/><span>Cargando trunks...</span></div>
      ) : filtered.length===0 ? (
        <div className="nv-card" style={{ textAlign:"center", padding:"48px 20px" }}>
          <div style={{ fontSize:32, opacity:.2, marginBottom:12 }}>⊕</div>
          <div style={{ fontSize:13, color:"var(--text-muted)", marginBottom:16 }}>{search?"Sin resultados":"Sin trunks configurados"}</div>
          {!search&&<button className="nv-btn nv-btn-primary" onClick={()=>setModal("crear")}>+ Crear primer trunk</button>}
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))", gap:14 }}>
          {filtered.map(t=>(
            <TrunkCard
              key={t.id}
              trunk={t}
              cdrStats={stats[t.nombre]}
              sbcInfo={null}
              sbcLive={false}
              onEdit={t=>{ setTarget(t); setModal("editar"); }}
              onToggle={handleToggle}
              onDelete={t=>setDelConf(t)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {(modal==="crear"||modal==="editar") && (
        <TrunkModal
          trunk={modal==="editar"?target:null}
          onClose={()=>setModal(null)}
          onSave={()=>{ setModal(null); load(); showMsg(modal==="crear"?"Trunk creado":"Trunk actualizado"); }}/>
      )}

      {delConf&&(
        <div className="nv-modal-overlay" onClick={()=>setDelConf(null)}>
          <div className="nv-modal" style={{ maxWidth:380 }} onClick={e=>e.stopPropagation()}>
            <div className="nv-modal-header">
              <span className="nv-modal-title">Eliminar trunk</span>
              <button className="nv-modal-close" onClick={()=>setDelConf(null)}>✕</button>
            </div>
            <p style={{ fontSize:13, color:"var(--text-secondary)", lineHeight:1.6 }}>
              ¿Eliminar el trunk <span style={{ fontFamily:"var(--font-mono)", color:"var(--danger)" }}>{delConf.nombre}</span>? Esta acción no se puede deshacer.
            </p>
            <div className="nv-modal-footer">
              <button className="nv-btn nv-btn-ghost" onClick={()=>setDelConf(null)}>Cancelar</button>
              <button className="nv-btn nv-btn-danger" onClick={()=>handleDelete(delConf)}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {confirmTrunk&&(
        <div className="nv-modal-overlay" onClick={()=>setConfirmTrunk(null)}>
          <div className="nv-modal" style={{ maxWidth:420 }} onClick={e=>e.stopPropagation()}>
            <div className="nv-modal-header">
              <span className="nv-modal-title">Desactivar troncal</span>
              <button className="nv-modal-close" onClick={()=>setConfirmTrunk(null)}>✕</button>
            </div>
            <p style={{ fontSize:13, color:"var(--text-secondary)", lineHeight:1.6 }}>
              Vas a desactivar la troncal <span style={{ fontFamily:"var(--font-mono)", color:"var(--warning)" }}>{confirmTrunk.nombre}</span>. Para confirmar, escribí la palabra <strong>desactivar</strong>:
            </p>
            <input className="nv-input" autoFocus value={confirmWord}
              onChange={e=>setConfirmWord(e.target.value)}
              placeholder="desactivar" style={{ marginBottom:14 }}
              onKeyDown={e=>{ if(e.key==="Enter" && confirmWord.trim().toLowerCase()==="desactivar"){ doToggle(confirmTrunk); setConfirmTrunk(null); } }}/>
            <div className="nv-modal-footer">
              <button className="nv-btn nv-btn-ghost" onClick={()=>setConfirmTrunk(null)}>Cancelar</button>
              <button className="nv-btn nv-btn-danger"
                disabled={confirmWord.trim().toLowerCase()!=="desactivar"}
                style={{ opacity: confirmWord.trim().toLowerCase()==="desactivar"?1:.5 }}
                onClick={()=>{ doToggle(confirmTrunk); setConfirmTrunk(null); }}>
                ⏸ Desactivar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
